import format from "date-fns/format"
import parseISO from "date-fns/parseISO"
import PropTypes from "prop-types"
import React, { useCallback, useContext, useEffect, useState } from "react"

import BasePopover from "src/components/BasePopover"
import Chips from "src/components/Chips"
import Form from "src/components/Form"
import SearchableSelect from "src/components/SearchableSelect"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "src/components/Select"

import useDebounce from "src/hooks/use_debounce"

import { ManageContractsContext } from ".."

const ContractsFilters = ({ table, filterOptions, trackEvent }) => {
  const { archivedGroupsPagePath } = useContext(ManageContractsContext)

  const [searchInputValue, setSearchInputValue] = useState(
    table.filters.searchTerm || ""
  )
  const [inMarinaStartDate, setInMarinaStartDate] = useState(
    table.filters.inMarinaStartDate
      ? parseISO(table.filters.inMarinaStartDate)
      : null
  )
  const [inMarinaEndDate, setInMarinaEndDate] = useState(
    table.filters.inMarinaEndDate
      ? parseISO(table.filters.inMarinaEndDate)
      : null
  )

  const [debouncedSetSearch] = useDebounce((value) => {
    table.setFilter("searchTerm", value)
  })

  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value
      setSearchInputValue(value)
      debouncedSetSearch(value.trim())
    },
    [debouncedSetSearch]
  )

  const handleStatusChange = (values) => {
    if (values.length > 0) {
      trackEvent("contracts:status_filter_pressed", {
        status: values[values.length - 1],
      })
    }
    table.setFilter("statuses", values)
  }

  const handleContractGroupChange = (values) => {
    if (values.length > 0) {
      trackEvent("contracts:contract_group_filter_pressed", {
        contract_group: values[values.length - 1],
      })
    }
    table.setFilter("contractGroups", values)
  }

  const handleInMarinaDateChange = (field, date) => {
    const formatted = date ? format(date, "yyyy-MM-dd") : null

    if (field === "inMarinaStartDate") {
      setInMarinaStartDate(date)
      if (!date) {
        setInMarinaEndDate(null)
        table.setFilters({ inMarinaStartDate: null, inMarinaEndDate: null })
      } else {
        table.setFilter("inMarinaStartDate", formatted)
      }
    } else {
      setInMarinaEndDate(date)
      if (!date) {
        setInMarinaStartDate(null)
        table.setFilters({ inMarinaStartDate: null, inMarinaEndDate: null })
      } else {
        table.setFilter("inMarinaEndDate", formatted)
      }
    }
  }

  useEffect(() => {
    setInMarinaStartDate(
      table.filters.inMarinaStartDate
        ? parseISO(table.filters.inMarinaStartDate)
        : null
    )
    setInMarinaEndDate(
      table.filters.inMarinaEndDate
        ? parseISO(table.filters.inMarinaEndDate)
        : null
    )
  }, [table.filters.inMarinaStartDate, table.filters.inMarinaEndDate])

  const buildChips = () => {
    const chips = []

    if (table.filters.searchTerm) {
      chips.push({
        key: "searchTerm",
        onClick: () => {
          table.clearFilter("searchTerm")
          setSearchInputValue("")
        },
        text: table.filters.searchTerm,
      })
    }

    if (table.filters.statuses?.length > 0) {
      table.filters.statuses.forEach((status) => {
        chips.push({
          key: "status-" + status,
          onClick: () =>
            table.setFilter(
              "statuses",
              table.filters.statuses.filter((s) => s !== status)
            ),
          text: status,
        })
      })
    }

    if (table.filters.contractGroups?.length > 0) {
      table.filters.contractGroups.forEach((groupId) => {
        chips.push({
          key: "group-" + groupId,
          onClick: () =>
            table.setFilter(
              "contractGroups",
              table.filters.contractGroups.filter((g) => g !== groupId)
            ),
          text:
            filterOptions.contractGroupOptions.find(
              (option) => option.id === groupId
            )?.name || groupId,
        })
      })
    }

    if (table.filters.inMarinaStartDate && table.filters.inMarinaEndDate) {
      const start = format(
        parseISO(table.filters.inMarinaStartDate),
        "MM/dd/yyyy"
      )
      const end = format(parseISO(table.filters.inMarinaEndDate), "MM/dd/yyyy")
      chips.push({
        key: "inMarina",
        text: `In marina: ${start} – ${end}`,
        onClick: () => {
          setInMarinaStartDate(null)
          setInMarinaEndDate(null)
          table.setFilters({ inMarinaStartDate: null, inMarinaEndDate: null })
        },
      })
    }

    return chips
  }

  const chips = buildChips()

  return (
    <div>
      <div className="no-print flex w-full flex-wrap gap-2 pt-1">
        <div className="w-full md:w-[336px]">
          <Form.IconTextField
            inputMode="text"
            name="searchQuery"
            onChange={handleSearchChange}
            placeholder="Search for a contract"
            type="text"
            value={searchInputValue}
            position="left"
            icon={<i className="fa-kit fa-search-mdi text-xl text-gray-400" />}
          />
        </div>
        <div className="flex gap-2">
          <Select
            multiple
            value={table.filters.statuses}
            onValueChange={handleStatusChange}
          >
            <SelectTrigger variant="button">Status</SelectTrigger>
            <SelectContent>
              {filterOptions.statusOptions.map((option) => (
                <SelectItem key={option.name} value={option.name}>
                  {option.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <SearchableSelect
            multiple
            value={table.filters.contractGroups}
            onValueChange={handleContractGroupChange}
          >
            <SearchableSelect.Trigger>Contract Group</SearchableSelect.Trigger>
            <SearchableSelect.FilteredList
              options={filterOptions.contractGroupOptions}
              placeholder="Search contract groups"
              footer={
                <div className="border-t px-3 py-2 text-xs text-gray-600">
                  Archived groups aren&apos;t shown here. Access them on the{" "}
                  <a className="font-semibold" href={archivedGroupsPagePath}>
                    archived groups page
                  </a>
                </div>
              }
            />
          </SearchableSelect>
          <BasePopover label="In Marina">
            <div className="space-y-2 p-4">
              <div className="flex flex-col">
                <Form.Label htmlFor="inMarinaStartDate">Start Date</Form.Label>
                <Form.DatePicker
                  value={inMarinaStartDate}
                  onChange={(date) =>
                    handleInMarinaDateChange("inMarinaStartDate", date)
                  }
                  id="inMarinaStartDate"
                  maxDate={inMarinaEndDate}
                  todayButton="Today"
                  portalId={null}
                />
              </div>
              <div className="flex flex-col">
                <Form.Label htmlFor="inMarinaEndDate">End Date</Form.Label>
                <Form.DatePicker
                  value={inMarinaEndDate}
                  onChange={(date) =>
                    handleInMarinaDateChange("inMarinaEndDate", date)
                  }
                  id="inMarinaEndDate"
                  minDate={inMarinaStartDate}
                  todayButton="Today"
                  portalId={null}
                />
              </div>
            </div>
          </BasePopover>
        </div>
      </div>
      {chips.length > 0 && (
        <Chips
          onReset={() => {
            table.clearAllFilters()
            setSearchInputValue("")
            setInMarinaStartDate(null)
            setInMarinaEndDate(null)
          }}
        >
          {chips.map((chip) => (
            <Chips.Chip
              key={chip.key}
              text={chip.text}
              onClick={chip.onClick}
            />
          ))}
        </Chips>
      )}
    </div>
  )
}

export default ContractsFilters

ContractsFilters.propTypes = {
  table: PropTypes.object.isRequired,
  filterOptions: PropTypes.shape({
    contractGroupOptions: PropTypes.array,
    statusOptions: PropTypes.array,
  }).isRequired,
  trackEvent: PropTypes.func.isRequired,
}
